import { useId, type MouseEvent, type ReactNode } from 'react';
import { ArrowDown, ArrowSwapVertical, ArrowUp } from 'iconsax-reactjs';
import { Checkbox } from '../Form/Checkbox';
import { EmptyState } from '../EmptyState/EmptyState';
import { ErrorState } from '../ErrorState/ErrorState';
import { cx } from '../internal/cx';
import { Skeleton } from '../Skeleton/Skeleton';
import styles from './DataTable.module.css';

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  key: string;
  direction: SortDirection;
}

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  /** CSS width, e.g. "12rem" or "20%". */
  width?: string;
  /** Render this column's cells as row headers (<th scope="row">), e.g. the name column. */
  rowHeader?: boolean;
  /** Visually hide the header text (it stays available to screen readers). */
  hideHeader?: boolean;
  className?: string;
}

export interface DataTableError {
  title?: string;
  message?: string;
  requestId?: string | null;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  getRowId: (row: T) => string;
  /** Describes the table for screen readers. Visually hidden unless `showCaption`. */
  caption: ReactNode;
  showCaption?: boolean;
  /** Controlled sort. The table never sorts data itself (server-driven). */
  sort?: SortState | null;
  onSortChange?: (sort: SortState) => void;
  loading?: boolean;
  loadingRowCount?: number;
  error?: DataTableError | string | null;
  onRetry?: () => void;
  /** Shown when there are no rows (and not loading / errored). */
  emptyState?: ReactNode;
  /** Enables row selection with checkboxes (controlled). */
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  isRowSelectable?: (row: T) => boolean;
  /** Plain-text row name used in checkbox labels ("Select Jane Doe"). */
  getRowLabel?: (row: T) => string;
  /**
   * Row activation. The primary column (first `rowHeader` column, else the first column) is
   * rendered as a button for keyboard users; the rest of the row is clickable with a pointer.
   * Keep that column's cell free of other interactive elements.
   */
  onRowClick?: (row: T) => void;
  /** Accessible label for the primary-cell button, e.g. (row) => `Open ${row.name}`. */
  getRowActionLabel?: (row: T) => string;
  density?: 'comfortable' | 'compact';
  /** Constrains height so the sticky header works inside the table's own scroll area. */
  maxHeight?: string;
  stickyHeader?: boolean;
  rowClassName?: (row: T) => string | undefined;
  className?: string;
}

const SCROLL_REGION_TAB_INDEX = 0;

const INTERACTIVE_SELECTOR =
  'a, button, input, select, textarea, label, [role="button"], [role="link"]';

function ariaSortValue(active: boolean, direction: SortDirection | undefined) {
  if (!active) return 'none' as const;
  return direction === 'asc' ? ('ascending' as const) : ('descending' as const);
}

function normalizeError(error: DataTableError | string): DataTableError {
  return typeof error === 'string' ? { message: error } : error;
}

export function DataTable<T>({
  columns,
  rows,
  getRowId,
  caption,
  showCaption = false,
  sort = null,
  onSortChange,
  loading = false,
  loadingRowCount = 5,
  error,
  onRetry,
  emptyState,
  selectedIds,
  onSelectionChange,
  isRowSelectable,
  getRowLabel,
  onRowClick,
  getRowActionLabel,
  density = 'comfortable',
  maxHeight,
  stickyHeader = true,
  rowClassName,
  className,
}: DataTableProps<T>) {
  const captionId = useId();
  const selectable = Boolean(selectedIds && onSelectionChange);
  const selected = new Set(selectedIds ?? []);
  const colCount = columns.length + (selectable ? 1 : 0);
  const primaryKey = (columns.find((c) => c.rowHeader) ?? columns[0])?.key;

  const selectableRows = selectable
    ? rows.filter((row) => (isRowSelectable ? isRowSelectable(row) : true))
    : [];
  const pageIds = selectableRows.map(getRowId);
  const selectedOnPage = pageIds.filter((id) => selected.has(id)).length;
  const allSelected = pageIds.length > 0 && selectedOnPage === pageIds.length;
  const someSelected = selectedOnPage > 0 && !allSelected;

  function toggleAll() {
    if (!onSelectionChange) return;
    if (allSelected) {
      const pageSet = new Set(pageIds);
      onSelectionChange((selectedIds ?? []).filter((id) => !pageSet.has(id)));
    } else {
      const next = new Set(selectedIds ?? []);
      for (const id of pageIds) next.add(id);
      onSelectionChange(Array.from(next));
    }
  }

  function toggleRow(id: string, checked: boolean) {
    if (!onSelectionChange) return;
    const current = selectedIds ?? [];
    onSelectionChange(checked ? [...current, id] : current.filter((x) => x !== id));
  }

  function onSortClick(column: DataTableColumn<T>) {
    if (!onSortChange) return;
    const direction: SortDirection =
      sort?.key === column.key && sort.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ key: column.key, direction });
  }

  function onRowMouseClick(event: MouseEvent<HTMLTableRowElement>, row: T) {
    if (!onRowClick) return;
    const target = event.target as HTMLElement;
    // Let real controls inside the row do their own thing; the primary button handles itself.
    if (target.closest(INTERACTIVE_SELECTOR)) return;
    if (window.getSelection()?.toString()) return; // Allow selecting text without navigating.
    onRowClick(row);
  }

  const errorInfo = error ? normalizeError(error) : null;

  let body: ReactNode;
  if (loading) {
    body = Array.from({ length: loadingRowCount }, (_, rowIndex) => (
      <tr key={`skeleton-${rowIndex}`} className={styles.row} aria-hidden="true">
        {selectable && (
          <td className={styles.selectCell}>
            <Skeleton variant="rect" width="1rem" height="1rem" />
          </td>
        )}
        {columns.map((column, colIndex) => (
          <td key={column.key} className={cx(styles.cell, column.align && styles[column.align])}>
            <Skeleton variant="text" width={`${55 + ((rowIndex * 7 + colIndex * 13) % 40)}%`} />
          </td>
        ))}
      </tr>
    ));
  } else if (errorInfo) {
    body = (
      <tr>
        <td colSpan={colCount} className={styles.stateCell}>
          <ErrorState
            size="sm"
            title={errorInfo.title ?? 'Could not load data'}
            message={errorInfo.message}
            requestId={errorInfo.requestId}
            onRetry={onRetry}
          />
        </td>
      </tr>
    );
  } else if (rows.length === 0) {
    body = (
      <tr>
        <td colSpan={colCount} className={styles.stateCell}>
          {emptyState ?? (
            <EmptyState
              size="sm"
              title="No records to show"
              description="Try adjusting your search or filters."
            />
          )}
        </td>
      </tr>
    );
  } else {
    body = rows.map((row) => {
      const id = getRowId(row);
      const isSelected = selected.has(id);
      const canSelect = isRowSelectable ? isRowSelectable(row) : true;
      return (
        // Pointer-only convenience; keyboard users activate the row via the primary-cell button.
        <tr
          key={id}
          className={cx(
            styles.row,
            isSelected && styles.selected,
            onRowClick && styles.clickable,
            rowClassName?.(row),
          )}
          onClick={onRowClick ? (event) => onRowMouseClick(event, row) : undefined}
        >
          {selectable && (
            <td className={styles.selectCell}>
              <Checkbox
                aria-label={`Select ${getRowLabel ? getRowLabel(row) : 'row'}`}
                checked={isSelected}
                disabled={!canSelect}
                onCheckedChange={(checked) => toggleRow(id, checked)}
              />
            </td>
          )}
          {columns.map((column) => {
            const content = column.cell(row);
            const isPrimary = onRowClick && column.key === primaryKey;
            const inner = isPrimary ? (
              <button
                type="button"
                className={styles.rowButton}
                aria-label={getRowActionLabel?.(row)}
                onClick={() => onRowClick(row)}
              >
                {content}
              </button>
            ) : (
              content
            );
            const cellClass = cx(
              styles.cell,
              column.align && styles[column.align],
              column.rowHeader && styles.rowHeader,
              column.className,
            );
            return column.rowHeader ? (
              <th key={column.key} scope="row" className={cellClass}>
                {inner}
              </th>
            ) : (
              <td key={column.key} className={cellClass}>
                {inner}
              </td>
            );
          })}
        </tr>
      );
    });
  }

  return (
    <div className={cx(styles.container, className)}>
      <div
        className={cx(styles.scroll, maxHeight && styles.scrollY)}
        style={maxHeight ? { maxHeight } : undefined}
        role="region"
        aria-labelledby={captionId}
        // Scrollable regions must be keyboard reachable (WCAG 2.1.1).
        tabIndex={SCROLL_REGION_TAB_INDEX}
      >
        <table
          className={cx(styles.table, styles[density], stickyHeader && styles.sticky)}
          aria-busy={loading || undefined}
        >
          <caption id={captionId} className={showCaption ? styles.caption : 'sr-only'}>
            {caption}
          </caption>
          <colgroup>
            {selectable && <col className={styles.selectCol} />}
            {columns.map((column) => (
              <col key={column.key} style={column.width ? { width: column.width } : undefined} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {selectable && (
                <th scope="col" className={styles.selectCell}>
                  <Checkbox
                    aria-label="Select all rows on this page"
                    checked={allSelected}
                    indeterminate={someSelected}
                    disabled={loading || pageIds.length === 0}
                    onCheckedChange={toggleAll}
                  />
                </th>
              )}
              {columns.map((column) => {
                const isSorted = sort?.key === column.key;
                const headerContent = column.hideHeader ? (
                  <span className="sr-only">{column.header}</span>
                ) : (
                  column.header
                );
                return (
                  <th
                    key={column.key}
                    scope="col"
                    className={cx(styles.headerCell, column.align && styles[column.align])}
                    aria-sort={
                      column.sortable ? ariaSortValue(isSorted, sort?.direction) : undefined
                    }
                  >
                    {column.sortable && onSortChange ? (
                      <button
                        type="button"
                        className={cx(styles.sortButton, isSorted && styles.sorted)}
                        onClick={() => onSortClick(column)}
                      >
                        <span>{headerContent}</span>
                        <span className={styles.sortIcon} aria-hidden="true">
                          {isSorted ? (
                            sort?.direction === 'asc' ? (
                              <ArrowUp size={14} color="currentColor" />
                            ) : (
                              <ArrowDown size={14} color="currentColor" />
                            )
                          ) : (
                            <ArrowSwapVertical size={14} color="currentColor" />
                          )}
                        </span>
                      </button>
                    ) : (
                      headerContent
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>{body}</tbody>
        </table>
      </div>
      {loading && (
        <span className="sr-only" role="status">
          Loading
        </span>
      )}
    </div>
  );
}
